import React from 'react';
import { Pressable, ScrollView, Text, View, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { Paprika } from '../../../styles/theme';
import { FAN_REASONS, DAY_PROFILE, dayFullKey } from '../../weekly/v2/constants';
import { useTranslation } from 'react-i18next';
import { TimeChip } from '../../weekly/v2/primitives/TimeChip';
import type { RecipeIndexEntry } from '../../../services/unifiedRecipeService';
import { RemoteImage } from '../../shared/RemoteImage';

export interface FanOption {
  key: string;
  recipe: RecipeIndexEntry;
  consequence?: string;
}

interface Props {
  di: number;
  options: FanOption[];
  onSelect: (recipeId: string) => void;
  onClose: () => void;
}

export function FanDrawer({ di, options, onSelect, onClose }: Props) {
  const profile = DAY_PROFILE[di];
  const { t } = useTranslation();
  const dayLabel = t(dayFullKey(di));

  return (
    <>
      <Pressable style={styles.scrim} onPress={onClose} />

      <View style={styles.drawer}>
        {/* Header */}
        <View style={styles.header}>
          <View>
            <Text style={styles.dayLabel}>{dayLabel}</Text>
            <View style={[styles.profileBadge, { backgroundColor: profile.accent + '22' }]}>
              <Text style={[styles.profileTag, { color: profile.accent }]}>{t(profile.tagKey)}</Text>
              <Text style={[styles.profileNote, { color: profile.accent + 'cc' }]}> · {t(profile.noteKey)}</Text>
            </View>
          </View>
          <Pressable style={styles.closeBtn} onPress={onClose}>
            <Feather name="x" size={14} color={Paprika.textSecondary} />
          </Pressable>
        </View>

        <ScrollView style={styles.scroll} showsVerticalScrollIndicator={false}>
          {FAN_REASONS.map((reason, i) => {
            const opt = options[i];
            if (!opt) return null;
            return (
              <Pressable key={reason.key} style={styles.row} onPress={() => onSelect(opt.recipe.id)}>
                {/* Colored left stripe */}
                <View style={[styles.stripe, { backgroundColor: reason.accent }]} />

                <View style={styles.photoWrap}>
                  <RemoteImage
                    uri={opt.recipe.image}
                    variant="thumb"
                    style={styles.photo}
                    resizeMode="cover"
                    fallback={<View style={[styles.photo, styles.photoPlaceholder]} />}
                  />
                </View>

                <View style={styles.rowInfo}>
                  <View style={[styles.angleChip, { backgroundColor: reason.accent + '22' }]}>
                    <Text style={[styles.angleChipText, { color: reason.accent }]}>{t(reason.labelKey)}</Text>
                  </View>
                  <Text style={styles.recipeTitle} numberOfLines={2}>{opt.recipe.title}</Text>
                  <View style={styles.rowMeta}>
                    <TimeChip minutes={opt.recipe.readyInMinutes} />
                    {!!opt.consequence && (
                      <Text style={styles.consequence} numberOfLines={1}>{opt.consequence}</Text>
                    )}
                  </View>
                </View>
              </Pressable>
            );
          })}
        </ScrollView>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  scrim: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(27, 28, 26, 0.24)',
  } as any,
  drawer: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: 440,
    backgroundColor: Paprika.surfaceLowest,
    shadowColor: 'rgba(82, 68, 60, 0.14)',
    shadowOffset: { width: -4, height: 0 },
    shadowOpacity: 1,
    shadowRadius: 24,
    elevation: 24,
    zIndex: 63,
  } as any,
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: 24,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.border,
  },
  dayLabel: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 20,
    color: Paprika.textPrimary,
    marginBottom: 6,
  },
  profileBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    alignSelf: 'flex-start',
  },
  profileTag: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 12,
  },
  profileNote: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
  },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: Paprika.surfaceLow,
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeBtnText: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    color: Paprika.textSecondary,
  },
  scroll: {
    flex: 1,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'stretch',
    borderBottomWidth: 1,
    borderBottomColor: Paprika.border,
    overflow: 'hidden',
  },
  stripe: {
    width: 4,
  },
  photoWrap: {
    width: 96,
    height: 96,
    flexShrink: 0,
  },
  photo: {
    width: 96,
    height: 96,
  },
  photoPlaceholder: {
    backgroundColor: Paprika.surfaceLow,
  },
  rowInfo: {
    flex: 1,
    padding: 12,
    gap: 6,
    justifyContent: 'center',
  },
  angleChip: {
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
  },
  angleChipText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 11,
  },
  recipeTitle: {
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    color: Paprika.textPrimary,
    lineHeight: 19,
  },
  rowMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  consequence: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: Paprika.textMuted,
    flex: 1,
  },
});
